/**
 * Simple Cart Store for Demo
 * 
 * Simplified Zustand cart store without API dependencies for reliable demo testing.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Product, ProductVariant } from '../types/ecommerce';

interface CartItem {
  productId: string;
  quantity: number;
  variant?: ProductVariant;
  addedAt: Date;
}

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
  isLoading: boolean;
  error: string | null;
}

interface CartActions {
  addItem: (product: Product, quantity: number, variant?: ProductVariant) => Promise<void>;
  removeItem: (productId: string, variantId?: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => Promise<void>;
  clearCart: () => Promise<void>;
  calculateTotals: (products: Record<string, Product>) => void;
  isItemInCart: (productId: string, variantId?: string) => boolean;
  getCartItem: (productId: string, variantId?: string) => CartItem | undefined;
}

type CartStore = CartState & CartActions;

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // State
      items: [],
      total: 0,
      itemCount: 0,
      isLoading: false,
      error: null,

      // Actions
      addItem: async (product: Product, quantity: number, variant?: ProductVariant) => {
        try {
          set({ isLoading: true, error: null });

          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 500));

          const state = get();
          const existingItemIndex = state.items.findIndex(item => 
            item.productId === product.id && 
            (!variant || item.variant?.id === variant.id)
          );

          let newItems: CartItem[];
          
          if (existingItemIndex >= 0) {
            // Update existing item
            newItems = [...state.items];
            newItems[existingItemIndex] = {
              ...newItems[existingItemIndex],
              quantity: newItems[existingItemIndex].quantity + quantity
            };
          } else {
            // Add new item
            const newItem: CartItem = {
              productId: product.id,
              quantity,
              variant,
              addedAt: new Date()
            };
            newItems = [...state.items, newItem];
          }

          // Calculate totals
          const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const total = newItems.reduce((sum, item) => {
            const price = item.variant?.price || product.price;
            return sum + (price * item.quantity);
          }, 0);

          set({
            items: newItems,
            itemCount,
            total,
            isLoading: false,
            error: null
          });

        } catch (error) {
          console.error('Failed to add item:', error);
          set({ 
            isLoading: false, 
            error: 'Failed to add item to cart' 
          });
        }
      },

      removeItem: async (productId: string, variantId?: string) => {
        try {
          set({ isLoading: true, error: null });

          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 300));

          const state = get();
          const newItems = state.items.filter(item => 
            !(item.productId === productId && 
              (!variantId || item.variant?.id === variantId))
          );

          const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

          set({
            items: newItems,
            itemCount,
            isLoading: false,
            error: null
          });

        } catch (error) {
          console.error('Failed to remove item:', error);
          set({ 
            isLoading: false, 
            error: 'Failed to remove item from cart' 
          });
        }
      },

      updateQuantity: async (productId: string, quantity: number, variantId?: string) => {
        try {
          set({ isLoading: true, error: null });

          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 300));

          const state = get();
          
          if (quantity <= 0) {
            // Remove item if quantity is 0 or less
            await state.removeItem(productId, variantId);
            return;
          }

          const newItems = state.items.map(item => {
            if (item.productId === productId && 
                (!variantId || item.variant?.id === variantId)) {
              return { ...item, quantity };
            }
            return item;
          });

          const itemCount = newItems.reduce((sum, item) => sum + item.quantity, 0);

          set({
            items: newItems,
            itemCount,
            isLoading: false,
            error: null
          });

        } catch (error) {
          console.error('Failed to update quantity:', error);
          set({ 
            isLoading: false, 
            error: 'Failed to update item quantity' 
          });
        }
      },

      clearCart: async () => {
        try {
          set({ isLoading: true, error: null });

          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 300));

          set({
            items: [],
            total: 0,
            itemCount: 0,
            isLoading: false,
            error: null
          });

        } catch (error) {
          console.error('Failed to clear cart:', error);
          set({ 
            isLoading: false, 
            error: 'Failed to clear cart' 
          });
        }
      },

      calculateTotals: (products: Record<string, Product>) => {
        const state = get();
        let total = 0;
        let itemCount = 0;

        state.items.forEach(item => {
          const product = products[item.productId];
          if (product) {
            const price = item.variant?.price || product.price;
            total += price * item.quantity;
            itemCount += item.quantity;
          }
        });

        set({ total, itemCount });
      },

      isItemInCart: (productId: string, variantId?: string) => {
        const state = get();
        return state.items.some(item => 
          item.productId === productId && 
          (!variantId || item.variant?.id === variantId)
        );
      },

      getCartItem: (productId: string, variantId?: string) => {
        const state = get();
        return state.items.find(item => 
          item.productId === productId && 
          (!variantId || item.variant?.id === variantId)
        );
      }
    }),
    {
      name: 'cart-storage', // localStorage key
      partialize: (state) => ({ 
        items: state.items,
        total: state.total,
        itemCount: state.itemCount
      }),
    }
  )
);

// Selectors for convenient access
export const cartSelectors = {
  items: (state: CartStore) => state.items,
  total: (state: CartStore) => state.total,
  itemCount: (state: CartStore) => state.itemCount,
  isLoading: (state: CartStore) => state.isLoading,
  error: (state: CartStore) => state.error,
  isAddingItem: (state: CartStore) => state.isLoading, // For backward compatibility
};

// Expose store to window in development mode for StateExporter
if (import.meta.env.DEV) {
  if (!window.__zustandStores) {
    window.__zustandStores = {};
  }
  window.__zustandStores.cart = useCartStore;
}